import { Star } from "@phosphor-icons/react";

const SHIELD_CLIP = "polygon(50% 0%, 100% 16%, 100% 62%, 50% 100%, 0% 62%, 0% 16%)";

const SIZES = {
  sm: { box: "w-32", minH: "min-h-[148px]", title: "text-[6px]", ribbon: "text-[7px] py-1", star5: 11, score: "text-lg" },
  md: { box: "w-44", minH: "min-h-[204px]", title: "text-[8px]", ribbon: "text-[9px] py-1.5", star5: 14, score: "text-2xl" },
  lg: { box: "w-60", minH: "min-h-[278px]", title: "text-[10px]", ribbon: "text-xs py-2", star5: 19, score: "text-4xl" },
};

/**
 * oodel SCORE™ badge — navy/gold shield showing a business's average
 * rating. Used on the oodel SCORE directory cards + individual business
 * profile pages. Purely presentational — caller supplies avgStars.
 *
 * The "based on N customer reviews" line is intentionally NOT rendered
 * inside this shield — the shield's clip-path narrows to a point well
 * before a text row placed that low would fit inside its visible outline,
 * so it always looked like it was spilling out the bottom. Callers render
 * that line themselves, below/beside the badge, using <ReviewCount />.
 */
export function ScoreBadge({ avgStars = 0, size = "md", testid = "score-badge" }) {
  const s = SIZES[size] || SIZES.md;
  const full5 = Math.round(avgStars || 0);
  return (
    <div className={`relative ${s.box} ${s.minH} shrink-0`} data-testid={testid}>
      <div className="absolute inset-0" style={{ clipPath: SHIELD_CLIP, background: "linear-gradient(160deg, #E9C767 0%, #B4872A 100%)" }} />
      <div className="absolute inset-[3px]" style={{ clipPath: SHIELD_CLIP, background: "#0B1424" }} />
      {/* Content is anchored to the shield's full-width "safe band" (16%-62% of
          the total height, matching SHIELD_CLIP) using absolute top/bottom
          percentages — these resolve against the box's real height, unlike
          padding-top/bottom (which resolves against width, a common CSS trap
          that previously pushed the title text up into the shield's pointed
          tip and made it look like it was spilling outside the outline). */}
      <div className="absolute inset-x-0 z-10 flex flex-col items-center justify-center gap-1 px-2.5" style={{ top: "17%", bottom: "39%" }}>
        <div className={`${s.title} font-bold tracking-[0.15em] text-white/80 text-center leading-tight`}>CUSTOMER SENTIMENT</div>
        <div className={`w-[92%] bg-gradient-to-r from-[#B4872A] via-[#E9C767] to-[#B4872A] ${s.ribbon} text-center font-black text-[#0B1424] tracking-tight leading-tight`}>
          POWERED BY oodel<span className="text-[#0B1424]">.</span>
        </div>
        <div className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={s.star5} weight="fill" className={i <= full5 ? "text-[#E9C767]" : "text-white/15"} />)}
        </div>
        <div className={`${s.score} font-black text-white leading-none`}>{avgStars ? avgStars.toFixed(1) : "—"}<span className="text-white/50 text-[0.5em]">/5</span></div>
      </div>
    </div>
  );
}

/** "Based on N customer reviews" — rendered OUTSIDE the shield by callers. */
export function ReviewCount({ totalResponses, className = "" }) {
  if (totalResponses == null) return null;
  return (
    <div className={`text-xs font-bold tracking-wide text-white/40 text-center ${className}`}>
      BASED ON {totalResponses} CUSTOMER {totalResponses === 1 ? "REVIEW" : "REVIEWS"}
    </div>
  );
}
